/*
 * Copyright 2026 Sony Corporation
 */
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/auth-context';
import type { MfaRequired, NewPasswordRequired } from '../services/cognito';

type Step = { kind: 'credentials' } | NewPasswordRequired | MfaRequired;

export const Login = () => {
  const { signedIn, signIn, confirmSignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/projects';

  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (signedIn) {
    return <Navigate to={from} replace />;
  }

  const finish = () => {
    confirmSignedIn();
    navigate(from, { replace: true });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (step.kind === 'credentials') {
        const result = await signIn(email.trim(), secret);
        if (result.kind === 'signed_in') {
          finish();
          return;
        }
        setStep(result);
      } else {
        await step.complete(secret);
        finish();
        return;
      }
      setSecret('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  const label =
    step.kind === 'credentials' ? 'Password' : step.kind === 'new_password_required' ? 'New password' : 'Code';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
      <Paper component="form" onSubmit={(event) => void submit(event)} sx={{ p: 4, width: 380 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Just-in-time App</Typography>
          {step.kind === 'new_password_required' && (
            <Alert severity="info">Choose a new password to finish setting up your account.</Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {step.kind === 'credentials' && (
            <TextField
              label="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
            />
          )}
          <TextField
            key={step.kind}
            label={label}
            type={step.kind === 'mfa_required' ? 'text' : 'password'}
            autoComplete={step.kind === 'credentials' ? 'current-password' : 'one-time-code'}
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
            required
            autoFocus={step.kind !== 'credentials'}
          />
          <Button type="submit" variant="contained" disabled={busy}>
            {step.kind === 'credentials' ? 'Sign in' : 'Continue'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
